import { BookingStatusEnum, RefundStatusEnum } from 'api-hooks/booking/model';
import {
  useBookingCancel,
  usePrintBookingTicket,
} from 'api-hooks/booking/mutation';
import { getBookingsKey, useGetRefundProof } from 'api-hooks/booking/query';
import {
  DownloadCircleIcon,
  RefundCircleIcon,
  RescheduleCircleIcon,
} from 'assets/images/svg';
import NavigationEnum from 'common/navigation';
import { queryClient } from 'common/repositories/query-client';
import { string2money } from 'common/utils/string';
import { Popper } from 'components/commons/popper';
import Stack from 'components/commons/stack';
import Text from 'components/commons/text';
import Button from 'components/elements/button';
import Countdown from 'components/widgets/countdown';
import Gallery from 'components/widgets/gallery';
import { addHours, format } from 'date-fns';
import { enUS, id } from 'date-fns/locale';
import * as FileSaver from 'file-saver';
import useDialog from 'hooks/use-dialog';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { ArrowRight, ChevronDown, ChevronRight } from 'react-feather';
import typography from 'styles/typography';

import {
  BookingIdText,
  BookingLabelText,
  Card,
  CardFooter,
  CruiseContentText,
  CruiseText,
  DateText,
  FacilityContentText,
  Gap,
  HiddenGap,
  LocationText,
  PortText,
  PriceText,
  ReminderBox,
  SmallLabel,
  styles,
  TimeText,
  TopContainer,
} from './style';
import DetailPanel from '../detail-panel';
import { RefundStatusPill } from '../style';

// big dot &#8226;

interface Props {
  cruiseName: string;
  sourcePort: string;
  sourceLocation: string;
  destinationPort: string;
  destinationLocation: string;
  passenger?: string;
  vehicle?: string;
  facility?: string;
  departureTime: string;
  departureDayCycle: string;
  arrivalDate: Date;
  arrivalTime: string;
  arrivalDayCycle: string;
  price: string;
  bookingId: string;
  departureDate: Date;
  ticketId: string;
  timer?: Date;
  type: 'active' | 'pending' | 'history' | 'refund';
  refundStatus?: RefundStatusEnum | null;
  status?: BookingStatusEnum;
  departureTz?: string;
  arrivalTz?: string;
  onClickTicket?: () => void;
  code: string;
  refundRejectedReason?: string | null;
  voyageCode?: string;
}
interface PrintProps {
  bookingId: string;
  bookingCode: string;
}
function dataURLtoFile(dataurl, filename) {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}
function PrintButton(props: PrintProps) {
  const { bookingId, bookingCode } = props;
  const { t } = useTranslation();
  const { mutateAsync, isLoading } = usePrintBookingTicket();

  const onClickPrint = React.useCallback(async () => {
    try {
      const result = await mutateAsync({ bookingId });
      if (result) {
        const resultFile = dataURLtoFile(
          result,
          `booking-tickets-${bookingCode}`,
        );
        FileSaver.saveAs(resultFile, `booking-tickets-${bookingCode}`);
      }
    } catch (error) {}
  }, [bookingCode, bookingId, mutateAsync]);

  return (
    <Button
      variant="tertiary"
      onRenderLeft={<DownloadCircleIcon size={24} />}
      isLoading={isLoading}
      css={{ width: '100%', justifyContent: 'flex-start' }}
      onClick={onClickPrint}
    >
      {t('common:print_ticket')}
    </Button>
  );
}

export default function CheckBookingCard(props: Props) {
  const dialog = useDialog();
  const { t } = useTranslation();
  const { locale, push } = useRouter();
  const {
    arrivalTime,
    cruiseName,
    departureDate,
    departureTime,
    destinationLocation,
    destinationPort,
    passenger,
    price,
    sourceLocation,
    sourcePort,
    vehicle,
    // ticketId,
    facility,
    refundStatus,
    status,
    type,
    timer,
    onClickTicket,
    bookingId,
    code,
    arrivalDate,
    refundRejectedReason,
    arrivalDayCycle,
    departureDayCycle,
  } = props;
  const { mutateAsync } = useBookingCancel();
  const { data } = useGetRefundProof(
    { bookingId },
    { enabled: refundStatus === RefundStatusEnum.REFUNDED },
  );

  const isCreated = status === BookingStatusEnum.CREATED;
  const isRefunded =
    !!refundStatus && refundStatus === RefundStatusEnum.REFUNDED;

  const rescheduleable = React.useCallback(() => {
    const result =
      addHours(departureDate, -1.5) > new Date() && parseFloat(price) >= 50000;
    return result;
  }, [departureDate, price]);

  const refundable = React.useCallback(() => {
    const result =
      !refundStatus &&
      parseFloat(price) >= 80000 &&
      addHours(departureDate, -2) > new Date();

    return result;
  }, [departureDate, price, refundStatus]);

  const [isRefundable, setIsRefundable] = React.useState(refundable());
  const [isRescheduleable, setIsRescheduleable] = React.useState(
    rescheduleable(),
  );

  const refundPillVariantByStatus = React.useMemo(() => {
    switch (status!) {
      case BookingStatusEnum.COMPLETED:
        return 'success';
      case BookingStatusEnum.EXPIRED:
        return 'failed';
      case BookingStatusEnum.CREATED:
      case BookingStatusEnum.PROCESSING:
      case BookingStatusEnum.CANCELED:
      case BookingStatusEnum.RESCHEDULING:
      default:
        return 'pending';
    }
  }, [status]);

  const refundPillVariantByRefundStatus = React.useMemo(() => {
    switch (refundStatus) {
      case RefundStatusEnum.REFUNDED:
        return 'success';
      case RefundStatusEnum.REJECTED:
        return 'failed';
      case RefundStatusEnum.PENDING:
      default:
        return 'pending';
    }
  }, [refundStatus]);

  const formatDate = React.useCallback(
    (date: Date, isComplete = false) => {
      const formatDate = isComplete ? 'eeee, dd MMMM yyyy' : 'dd MMMM yyyy';
      return format(date, formatDate, {
        locale: locale === 'en' ? enUS : id,
      });
    },
    [locale],
  );

  const onClickRefundProof = React.useCallback(() => {
    return dialog.showCustom({
      render(close) {
        return (
          <Gallery
            onClose={close}
            imageList={(data?.data || []).map((image) => ({
              alt: image.name,
              src: image.url,
            }))}
          />
        );
      },
    });
  }, [data?.data, dialog]);

  const onClickCancelOrder = React.useCallback(() => {
    dialog.showConfirmation({
      title: t('common:confirmation'),
      message: t('common:cancel_confirmation_text'),
      onPositiveAction: async (dismiss) => {
        try {
          await mutateAsync(
            {
              bookingId: bookingId!,
            },
            {
              onSuccess: async () => {
                await queryClient.refetchQueries(
                  getBookingsKey({
                    params: { type: 'pending' },
                  }),
                );
              },
            },
          );
        } catch (error) {
        } finally {
          dismiss();
        }
      },
      onNegativeAction(dismiss) {
        dismiss();
      },
    });
  }, [bookingId, dialog, mutateAsync, t]);

  const onClickNavigateShipping = React.useCallback(() => {
    push({
      pathname: isCreated
        ? NavigationEnum.shippingDetail
        : NavigationEnum.shippingPayment,
      query: { id: props.bookingId },
    });
  }, [isCreated, props.bookingId, push]);

  const onClickRefund = React.useCallback(() => {
    push({
      pathname: NavigationEnum.refund,
      query: { id: props.bookingId },
    });
  }, [props.bookingId, push]);

  const onClickReschedule = React.useCallback(() => {
    push({
      pathname: NavigationEnum.reschedule,
      query: { id: props.bookingId },
    });
  }, [props.bookingId, push]);

  const onClickShowTicket = React.useCallback(() => {
    onClickTicket?.();
  }, [onClickTicket]);

  React.useEffect(() => {
    if (!isRefundable) return;

    const interval = setInterval(() => {
      const isRefundable = refundable();
      if (!isRefundable) {
        setIsRefundable(false);
        return clearInterval(interval);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [isRefundable, refundable]);

  React.useEffect(() => {
    if (!isRescheduleable) return;

    const interval = setInterval(() => {
      const isRescheduleable = rescheduleable();
      if (!isRescheduleable) {
        setIsRescheduleable(false);
        return clearInterval(interval);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [isRescheduleable, rescheduleable]);

  return (
    <Card>
      {timer && (
        <TopContainer>
          <ReminderBox>
            <Countdown
              expiredAt={timer}
              startText={
                isCreated
                  ? t('common:please_complete_your_data_in')
                  : t('common:please_complete_your_payment_in')
              }
              finishText=""
            />
          </ReminderBox>
        </TopContainer>
      )}
      <Stack gap="md" rootCss={styles.breakPoints}>
        <Stack.Item
          css={{
            display: 'flex',
            '@md': { display: 'none' },
          }}
        >
          <CruiseContentText>{cruiseName}</CruiseContentText>
        </Stack.Item>
        <Stack direction="horizontal" alignItems="center" justify="between">
          <Stack.Item direction="horizontal" color="typeGray">
            <BookingLabelText>
              {t('shipping:order_number')}:&nbsp;
            </BookingLabelText>
            <BookingIdText weight="bold">{code}</BookingIdText>
          </Stack.Item>
          <CruiseText>{cruiseName}</CruiseText>
        </Stack>
        <Stack rootCss={styles.bottomContainer} grow>
          <Stack grow gap="md">
            <Stack direction="horizontal" justify="between">
              <Stack.Item css={{ flex: 1, gap: 4 }}>
                <DateText css={{ display: 'none', '@md': { display: 'flex' } }}>
                  {formatDate(departureDate, true)}
                </DateText>
                <DateText css={{ display: 'flex', '@md': { display: 'none' } }}>
                  {formatDate(departureDate)}
                </DateText>
                <TimeText>{`${departureTime} ${departureDayCycle}`}</TimeText>
                <PortText>{sourcePort}</PortText>
                <LocationText>{sourceLocation}</LocationText>
              </Stack.Item>
              <Stack.Item css={{ gap: 4 }}>
                <Gap />
                <Stack.Item
                  css={{ display: 'none', '@md': { display: 'flex' } }}
                >
                  <ArrowRight />
                </Stack.Item>
                <Stack.Item
                  css={{ display: 'flex', '@md': { display: 'none' } }}
                >
                  <ArrowRight size={16} />
                </Stack.Item>
                <Gap />
                <Gap />
              </Stack.Item>
              <Stack.Item css={{ flex: 1, gap: 4 }}>
                <DateText css={{ display: 'none', '@md': { display: 'flex' } }}>
                  {formatDate(arrivalDate, true)}
                </DateText>
                <DateText css={{ display: 'flex', '@md': { display: 'none' } }}>
                  {formatDate(arrivalDate)}
                </DateText>
                <TimeText>{`${arrivalTime} ${arrivalDayCycle}`}</TimeText>
                <PortText>{destinationPort}</PortText>
                <LocationText>{destinationLocation}</LocationText>
              </Stack.Item>
            </Stack>
            <Stack.Item>
              <Stack.Item css={styles.passengerVehicleContainer}>
                {!!passenger && (
                  <Stack.Item css={{ '@md': { marginLeft: '0px !important' } }}>
                    <SmallLabel variant="body6">
                      {t('common:passenger_count')}
                    </SmallLabel>
                    <FacilityContentText>{passenger}</FacilityContentText>
                  </Stack.Item>
                )}
                {vehicle && (
                  <Stack.Item>
                    <SmallLabel variant="body6">
                      {t('common:vehicle_type')}
                    </SmallLabel>
                    <FacilityContentText>{vehicle}</FacilityContentText>
                  </Stack.Item>
                )}
                {facility && (
                  <Stack.Item>
                    <SmallLabel variant="body6">
                      {t('common:facility_type')}
                    </SmallLabel>
                    <FacilityContentText>{facility}</FacilityContentText>
                  </Stack.Item>
                )}
              </Stack.Item>
            </Stack.Item>
          </Stack>
          <Stack.Item gap="md" css={styles.rightSideContainer} wrap>
            <Stack.Item css={styles.priceContainer}>
              {!isCreated && <PriceText>Rp {string2money(price)}</PriceText>}
              {timer && (
                <ReminderBox
                  css={{
                    '@media(max-width: 1199px)': { display: 'none' },
                    mt: 16,
                    ml: 16,
                  }}
                >
                  <Countdown
                    expiredAt={timer}
                    startText={
                      isCreated
                        ? t('common:please_complete_your_data_in')
                        : t('common:please_complete_your_payment_in')
                    }
                    finishText=""
                  />
                </ReminderBox>
              )}
              {!isCreated ? (
                <DetailPanel
                  bookingId={props.bookingId}
                  trigger={(toggle) => (
                    <Button
                      onClick={toggle}
                      variant="tertiary"
                      onRenderRight={<ChevronRight />}
                      css={{
                        display: 'flex',
                        svg: {
                          width: 16,
                          height: 16,
                        },
                        '@md': {
                          display: 'none',
                        },
                      }}
                    >
                      {t('common:detail')}
                    </Button>
                  )}
                />
              ) : (
                <HiddenGap />
              )}
            </Stack.Item>
            {status && type === 'history' && (
              <Stack.Item
                css={{
                  mt: 8,
                  '@xl': {
                    mt: 0,
                  },
                }}
              >
                <RefundStatusPill variant={refundPillVariantByStatus}>
                  {t(`booking:booking_${status}`)}
                </RefundStatusPill>
              </Stack.Item>
            )}
            {refundStatus && (
              <Stack
                rootCss={{
                  mt: 8,
                  '@xl': {
                    mt: 0,
                  },
                }}
              >
                <RefundStatusPill variant={refundPillVariantByRefundStatus}>
                  {t(`booking:refund_${refundStatus}`)}
                </RefundStatusPill>
              </Stack>
            )}
            {refundRejectedReason && (
              <Text
                variant="body5"
                color="typeGray"
                css={{ mt: 8, '@md': typography.body3 }}
              >
                {t('booking:refund_rejected_reason', {
                  message: refundRejectedReason,
                })}
              </Text>
            )}
            {isRefunded && (
              <Button
                onRenderRight={<ChevronRight size={16} />}
                onClick={onClickRefundProof}
                css={{
                  display: 'none',
                  '@md': {
                    display: 'flex',
                    svg: {
                      width: 16,
                      height: 16,
                    },
                  },
                }}
                variant="tertiary"
              >
                {t('booking:refund_proof')}
              </Button>
            )}
            {isRefunded && (
              <Button
                size="medium"
                css={{
                  display: 'flex',
                  alignSelf: 'center',
                  '@md': {
                    display: 'none',
                    svg: {
                      width: 16,
                      height: 16,
                    },
                  },
                }}
                variant="tertiary"
                onClick={onClickRefundProof}
              >
                {t('booking:refund_proof')}
              </Button>
            )}
          </Stack.Item>
        </Stack>
      </Stack>

      <CardFooter
        css={
          type === 'refund' || type === 'history'
            ? {
                display: 'none',
                '@md': {
                  display: 'flex',
                },
              }
            : {}
        }
      >
        {!isCreated ? (
          <DetailPanel
            bookingId={props.bookingId}
            trigger={(toggle) => (
              <Button
                variant="tertiary"
                onRenderRight={<ChevronRight />}
                onClick={toggle}
                css={{
                  display: 'none',
                  '@md': {
                    display: 'flex',
                    svg: {
                      width: 16,
                      height: 16,
                    },
                  },
                }}
              >
                {t('common:order_detail')}
              </Button>
            )}
          />
        ) : (
          <HiddenGap />
        )}
        {(type === 'active' || type === 'pending') && (
          <Stack.Item
            direction="horizontal"
            css={{
              flexDirection: 'column-reverse',
              '@md': {
                flexDirection: 'row',
                flexBasis: 400,
              },
              justifyContent: 'flex-end',
            }}
          >
            {type === 'pending' && (
              <>
                {status === BookingStatusEnum.PROCESSING && (
                  <Stack.Item>
                    <Button
                      variant="tertiary"
                      css={{ width: '100%' }}
                      onClick={onClickCancelOrder}
                    >
                      {t('common:cancel_order')}
                    </Button>
                  </Stack.Item>
                )}
                <Stack.Item>
                  <Button
                    css={{ width: '100%' }}
                    onClick={onClickNavigateShipping}
                  >
                    {isCreated
                      ? t('common:continue_order')
                      : t('common:pay_now')}
                  </Button>
                </Stack.Item>
              </>
            )}
            {type === 'active' && (
              <>
                <Popper
                  css={{
                    flex: 1,
                    my: 4,
                    '@md': { my: 0, mx: 8 },
                  }}
                  childContainerCss={{
                    position: 'absolute !important',
                    top: 'inherit !important',
                    left: 'inherit !important',
                    border: '1px solid $dbGray1',
                    mt: 16,
                    borderRadius: '6px',
                    width: 200,
                  }}
                  trigger={(ref, toggle) => (
                    <Button
                      variant="secondary"
                      css={{
                        whiteSpace: 'nowrap',
                        width: '100%',
                      }}
                      onRenderRight={<ChevronDown />}
                      onClick={toggle}
                    >
                      {t('booking:manage_order')}
                    </Button>
                  )}
                >
                  {() => (
                    <>
                      <PrintButton bookingId={bookingId} bookingCode={code} />
                      {isRefundable && (
                        <Button
                          variant="tertiary"
                          onRenderLeft={<RefundCircleIcon size={24} />}
                          css={{
                            width: '100%',
                            justifyContent: 'flex-start',
                          }}
                          onClick={onClickRefund}
                        >
                          {t('common:ticket_refund')}
                        </Button>
                      )}
                      {isRescheduleable && (
                        <Button
                          variant="tertiary"
                          onRenderLeft={<RescheduleCircleIcon size={24} />}
                          css={{
                            width: '100%',
                            justifyContent: 'flex-start',
                          }}
                          onClick={onClickReschedule}
                        >
                          {t('common:reschedule')}
                        </Button>
                      )}
                    </>
                  )}
                </Popper>
                <Stack.Item css={{ flex: 1 }}>
                  <Button
                    css={{
                      width: '100%',
                    }}
                    onClick={onClickShowTicket}
                  >
                    {t('common:see_ticket')}
                  </Button>
                </Stack.Item>
              </>
            )}
          </Stack.Item>
        )}
      </CardFooter>
    </Card>
  );
}
